/**
 * iOS Audio Unlock Utility
 *
 * iOS Safari/Chrome block programmatic audio.play() unless it originates
 * from a user gesture. This module "unlocks" the audio pipeline by playing
 * a silent buffer during a real tap/click (e.g. joining a meeting).
 * Once unlocked, subsequent audio.play() calls succeed without gestures.
 */

let unlocked = false;

/**
 * Call this inside a click/tap handler (e.g. "Join Meeting" button).
 * It plays a tiny silent audio buffer which permanently unlocks
 * programmatic playback for the lifetime of the page.
 */
export function unlockIOSAudio() {
  if (unlocked) return;

  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    // Create a silent buffer (1 sample, 1 channel, at the context sample rate)
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);

    // Also unlock HTML5 Audio element playback
    const silentAudio = new Audio();
    silentAudio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    silentAudio.muted = true;
    silentAudio.playsInline = true;
    silentAudio.play().then(() => {
      silentAudio.pause();
      silentAudio.remove();
    }).catch(() => {});

    // Resume context if suspended (iOS pauses it by default)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    unlocked = true;
  } catch (e) {
    // Silent fail — non-critical
  }
}

export function isIOSAudioUnlocked() {
  return unlocked;
}

let videoUnlocked = false;

/**
 * Call inside a click/tap handler to unlock UNMUTED video playback on iOS.
 * Plays a tiny unmuted video element within the user gesture.
 */
export function unlockIOSVideo() {
  if (videoUnlocked) return;
  try {
    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.muted = false;
    video.volume = 0.01;
    video.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAAhtZGF0AAAA1m1vb3YAAABsbXZoZAAAAAAAAAAAAAAAAAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAABidWR0YQAAAFptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal0b28AAAAdZGF0YQAAAAEAAAAATGF2ZjYwLjMuMTAw';
    video.playsInline = true;
    video.style.position = 'fixed';
    video.style.top = '-9999px';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.opacity = '0';
    document.body.appendChild(video);
    video.play().then(() => {
      setTimeout(() => { video.pause(); video.remove(); }, 100);
      videoUnlocked = true;
    }).catch(() => { video.remove(); });
  } catch (e) {}
}

/**
 * Unlock both audio and video in a single user gesture.
 */
export function unlockIOSMedia() {
  unlockIOSAudio();
  unlockIOSVideo();
}

export function isIOSVideoUnlocked() {
  return videoUnlocked;
}

export function isIOSMediaUnlocked() {
  return unlocked && videoUnlocked;
}
